<template>
  <v-container fluid>
    <div class="login-wrapper">
      <v-form id="login-form" @submit.prevent="login" class="login px-5 py-3">
        <v-img max-width="250" v-show="$vuetify.breakpoint.mdAndUp" src="/img/veroFresh.png"/>
        <forgot-password v-if="showForgotPassword" @return-to-login="showForgotPassword = false"></forgot-password>
        <div v-else class="login-inputs">
          <div class="login-input py-2">
            <v-text-field
              prepend-inner-icon="mdi-account"
              id="email"
              :error-messages="emailErrors"
              v-model="email"
              :placeholder="$t('email')"
              solo />
          </div>
          <div class="login-input py-2">
            <v-text-field
              prepend-inner-icon="mdi-lock"
              id="password"
              :error-messages="passwordErrors"
              v-model="password"
              :placeholder="$t('password')"
              :append-icon="revealPassword ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append="() => (revealPassword = !revealPassword)"
              :type="revealPassword ? 'text' : 'password'"
              solo />
          </div>
          <div v-if="messages.basicAuthError" class="login-error py-1">
            {{ messages.basicAuthError }}
          </div>
          <!-- One-time Password/Security Code -->
          <div v-if="otpRequired" id="onetime-pass" class="validate-group py-2">
            <div v-if="resendingOtp" class="resending-onetime-pass pb-2">
              Sending...
            </div>
            <div v-else-if="messages.otpInfoMessage" class="security-code-message pb-2">
              {{ messages.otpInfoMessage }}
            </div>
            <div class="login-input">
              <v-text-field
                prepend-inner-icon="mdi-shield"
                :error-messages="otpErrors"
                v-model="otp"
                :placeholder="$t('security_code')"
                solo />
            </div>
            <div v-if="messages.otpAuthError" class="login-error py-1">
              {{ messages.otpAuthError }}
            </div>
            <v-btn text :href="`https://${zendeskDomain}/hc/en-us/articles/360038809794`" class="px-0">
              {{ $t('login_having_trouble') }}
            </v-btn>
            <v-btn text id="resend-otp" @click="resendOtp" class="px-0 float-right">
              {{ $t('login_send_another_code') }}
            </v-btn>
          </div>
          <v-switch
            id="keep-logged-in"
            :label="$t('keep_me_logged_in')"
            v-model="keepLoggedIn"
            :true-value="true"
            :false-value="false" />
          <v-btn type="submit" class="mx-auto my-2">
            <v-icon>mdi-login</v-icon>
            {{ $t('log_in') }}
          </v-btn>
          <br>
          <v-btn text class="d-block mx-auto" @click="showForgotPassword = true">
            {{ $t('forgot_your_password') }}
          </v-btn>
        </div>
      </v-form>
    </div>
  </v-container>
</template>

<script>
import axios from 'axios'
import ValidationMixin from '@/components/mixins/ValidationMixin'
import UrlHelper from '@/util/Url'
import AuthUtil from '@/util/AuthUtil'
import { required } from 'vuelidate/lib/validators'
import { isEmpty } from 'lodash'
import ForgotPassword from '@/components/site/ForgotPassword'

export default {
  name: 'Login',
  components: {
    ForgotPassword
  },
  mixins: [ValidationMixin],
  data () {
    return {
      // Auth API url base
      url: `${UrlHelper.getApiUrl()}/auth`,

      /** Login form state */
      email: null,
      password: null,
      // The one-time password
      otp: null,
      // Whether to keep the user logged in
      keepLoggedIn: false,
      // Whether to show the user's password
      revealPassword: false,

      /** UI state */
      // Misc State
      // Whether to show the "Forgot your password" portion of the interface
      showForgotPassword: false,
      // Whether the OTP is currently required in the interface
      otpRequired: false,
      // Whether we are currently sending a login request
      sendingLogin: false,
      // Whether we are requesting a new OTP
      resendingOtp: false,
      // State for the info/error messages on the page
      messages: {
        // Used for basic (username/login) auth errors
        basicAuthError: null,
        // Used for informational messages about the one-time pass
        otpInfoMessage: null,
        // Used for one-time pass error messages (such as if an error happened during generation)
        otpAuthError: null
      }
    }
  },
  validations: {
    email: {
      required
    },
    password: {
      required
    },
    otp: {
      required: function (value) {
        if (this.otpRequired) return !isEmpty(value)
        return true
      }
    }
  },
  created() {
    // Fire off a quick API check to see if we're in maintenance mode. If we get a 503,
    // the interceptor will take us to the maintenance page.
    this.$httpAdapter({url: 'site/status'})
    // Pre-populate email if available from query param
    if (this.$route.query.email) {
      this.email = this.$route.query.email
    }
    // Capture returnUrl from query param (for external redirects like chatbot)
    if (this.$route.query.returnUrl) {
      this.$store.dispatch('userModule/setReturnUrl', this.$route.query.returnUrl)
    }
  },
  computed: {
    zendeskDomain () {
      return process.env.VUE_APP_ZENDESK_DOMAIN
    },
    emailErrors () {
      return this.validationErrors([{
        prop: this.$v.email,
        rule: 'required',
        message: this.$t('required')
      }])
    },
    passwordErrors () {
      return this.validationErrors([{
        prop: this.$v.password,
        rule: 'required',
        message: this.$t('required')
      }])
    },
    otpErrors () {
      return this.validationErrors([{
        prop: this.$v.otp,
        rule: 'required',
        message: this.$t('required')
      }])
    },
  },
  methods: {
    /**
     * Perform a login request.
     */
    async login () {
      this.clearMessages()

      this.$v.$touch()
      if (this.$v.$invalid) {
        return false
      }

      this.sendingLogin = true

      const requestConfig = {
        auth: {
          username: this.email,
          password: this.password
        }
      }
      if (this.otp != null) {
        requestConfig.headers = {
          'X-Verofresh-OTP': this.otp
        }
      }
      // This login call is fairly specialized, so we use an axios instance
      // directly, instead of VerofreshHttpAdapter. This lets us handle 40x messages
      // as part of the login process rather than as an error.
      const response = await axios.get(this.url, requestConfig)
        .catch(err => err.response)
        .finally(() => {
          this.sendingLogin = false
        })

      if (response.status === 200) {
        // Successful login.
        const jwt = AuthUtil.parseJwt(response)
        AuthUtil.login(jwt, this.keepLoggedIn)
      } else if (response.status === 401) {
        const otpStatus = response.data.one_time_password
        // Basic Auth failure
        if (otpStatus == null) {
          this.otpRequired = false
          // Reset the OTP - it's probably rare that we actually need to do this.
          // The only time this would matter is if the user submitted valid credentials,
          // changed them to invalid and resubmitted, which we wouldn't normally expect.
          this.otp = null
          try {
            let messageJson = JSON.parse(response.data.message)
            this.messages.basicAuthError = this.$i18n.t(messageJson.name, {lockdata: messageJson.lockdata})
          } catch (e) {
            this.messages.basicAuthError = this.$i18n.t('login_error')
          }
          // Auth succeeded, OTP is required and was sent in response.
        } else if (otpStatus === 'sent') {
          this.otpRequired = true
          // If user tried to send the OTP but otpStatus is 'sent', that means that the backend regenerated and sent a
          // new OTP token because the old one is expired. We should have a message to that effect.
          if (this.otp) {
            this.messages.otpAuthError = this.$i18n.t('security_code_regenerated')
          } else {
            this.messages.otpInfoMessage = this.$t('security_code_message')
          }
          // Auth succeeded, A valid OTP already exists so none was sent.
        } else if (otpStatus === 'required') {
          this.otpRequired = true
          this.messages.otpInfoMessage = this.$i18n.t('security_code_message')
          // OTP was expired
        } else if (otpStatus === 'expired') {
          this.otpRequired = true
          this.messages.otpAuthError = this.$i18n.t('security_code_expired')
          // OTP was invalid
        } else if (otpStatus === 'invalid') {
          this.otpRequired = true
          this.messages.otpAuthError = this.$i18n.t('security_code_invalid')
        }
        // If we failed due to an OTP error, reset validation state.
        // This keeps a validation error from showing up on the OTP input before
        // we want to have it validated.
        if (this.otpRequired) {
          this.$v.$reset()
        }
      } else if (response.status === 503) {
        // Since we're using axios directly, we also have to manually handle
        // the unlikely case that the user received a 503 response.
        await this.$store.dispatch('globalMessagingModule/setMaintenanceMode', true)
      }
    },
    /**
     * Send an auth request with a specific header to trigger sending a new one-time password.
     */
    async resendOtp () {
      this.clearMessages()
      this.otp = null
      this.resendingOtp = true
      const requestConfig = {
        auth: {
          username: this.email,
          password: this.password
        },
        headers: {
          'X-Verofresh-OTP-Resend': true
        }
      }
      const response = await axios.get(this.url, requestConfig)
        .catch(err => err.response)
        .finally(() => {
          this.resendingOtp = false
        })

      // We are expecting a 401 response here. If the response's otp_status value is 'sent',
      // then we know the OTP was successfully sent.
      if (response.status === 401 && response.data.one_time_password === 'sent') {
        // success, do stuff
        this.messages.otpInfoMessage = this.$i18n.t('security_code_resent')
      } else {
        this.messages.otpAuthError = this.$i18n.t('security_code_error')
      }
    },
    clearMessages () {
      this.messages = {
        basicAuthError: null,
        otpAuthError: null,
        otpInfoMessage: null
      }
    }
  }
}

</script>

<style scoped lang="scss">

.container {
  padding: 0;
}

.resending-onetime-pass {
  color: rgb(51, 122, 183);
  font-size: 1.25rem;
  text-align: center;
}

.login .btn-link {
  font-size: smaller;
}

.security-code-message {
  font-size: .75rem;
}

.reset-password-info {
  font-size: .85em;
}

.login-input {
  width: 100%; /* Adjust the width as needed */
}

.v-input--selection-controls {
  margin-top: 0px !important; /* Override the top margin */
}
</style>
